import {
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';

import { translate } from '@jsverse/transloco';
import { debounceTime, distinctUntilChanged, Subject, takeUntil } from 'rxjs';
import { ErrorObject } from '@inmoment/the-kitchen';

import { RGBAColor } from '@inmoment/the-kitchen/src/lib/Pickers/ColorPicker/ColorPicker.model';

import { AppSharedModule } from 'app/app-shared.module';
import { FilterKind } from 'app/modules/collection/services/filter.service';
import {
  ComparisonFilterFieldExtra,
  ComparisonFilterSet,
} from 'app/providers/bridge-api/dashboard.service';
import {
  EnableClassTelemetry,
  EnableComponentTelemetry,
  injectComponentTelemetryService,
} from 'app/services/otel.service';
import { WidgetDataSource } from '../../../../models';
import { Utils } from '../../../../utils/utils';
import {
  ComparisonDateType,
  ComparisonFilterExtraDropdown,
  ComparisonSubType,
  ComparisonType,
} from '../create-comparison-drawer/create-comparison-drawer.component';
import { ComparisonFilterDateItemComponent } from './comparison-filter-date-item/comparison-filter-date-item.component';
import { ComparisonFilterDynamicDateItemComponent } from './comparison-filter-dynamic-date-item/comparison-filter-dynamic-date-item.component';
import { ComparisonFilterKeywordItemComponent } from './comparison-filter-keyword-item/comparison-filter-keyword-item.component';
import { ComparisonFilterNumericItemComponent } from './comparison-filter-numeric-item/comparison-filter-numeric-item.component';

export interface FilterHandler<XIIn, XIOut> {
  displayValue: XIIn;
  fromFilter(filter: ComparisonFilterSet): XIIn;
  toFilter(value: XIOut): ComparisonFilterSet;
  hasFilterValue(filter: ComparisonFilterSet): boolean;
}

@EnableClassTelemetry()
@Component({
  selector: 'app-comparison-filter-item',
  imports: [
    AppSharedModule,
    ComparisonFilterDateItemComponent,
    ComparisonFilterKeywordItemComponent,
    ComparisonFilterNumericItemComponent,
    ComparisonFilterDynamicDateItemComponent,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './comparison-filter-item.component.html',
  styleUrls: ['./comparison-filter-item.component.scss'],
})
export class ComparisonFilterItemComponent implements OnInit, OnDestroy {
  @EnableComponentTelemetry() otelService = injectComponentTelemetryService();
  @Input() type: ComparisonType;
  @Input() subType: ComparisonSubType;
  @Input() allFilters: ComparisonFilterSet[];
  @Input() dateType: ComparisonDateType;
  @Input() filters: ComparisonFilterSet;
  @Input() dataSource: WidgetDataSource;
  @Input() field: string;
  @Input() fieldExtra?: ComparisonFilterFieldExtra;
  @Input() projectId: string;
  @Input() isReadOnly: boolean;
  @Input() isDialog?: boolean;
  @Input() filtersLabel: string;
  @Input() filterLabelStatus: ErrorObject;
  @Input() filtersColor: string;
  @Input() filtersExtraDropdown?: ComparisonFilterExtraDropdown;
  @Input() projectColors: { color: RGBAColor }[];

  @Output() filtersChange = new EventEmitter();
  @Output() filtersLabelChange = new EventEmitter();
  @Output() filtersColorChange = new EventEmitter();
  @Output() filtersRemove = new EventEmitter();

  filterInputLabel: string;
  filterKind: FilterKind;
  colorChange$ = new Subject<RGBAColor>();
  destroy$ = new Subject<void>();

  constructor() {}

  ngOnInit(): void {
    if (this.type === 'date') {
      this.dateType =
        this.dateType === 'dynamicDate' || this.filters?.dynamicDate?.rangeNumber
          ? 'dynamicDate'
          : 'regularDate';
    } else if (this.subType === 'sectionDate') {
      this.dateType ??= this.filters?.subDateType;
    }
    this.filterKind = this.type === 'sectionFilters' ? 'comparisonSectionFilter' : undefined;

    const dateOptions = {
      dynamicDate: 'comparison-filter-item.dynamic-date-range',
      regularDate: 'comparison-filter-item.date-range',
    };

    const filterLabels = {
      date: dateOptions[this.dateType] || 'comparison-filter-item.date-range',
      keyword: 'comparison-filter-item.keyword',
      numeric: 'comparison-filter-item.numeric',
      sectionFilters: 'comparison-filter-item.sectionFilters',
    };

    this.filterInputLabel = filterLabels[this.type] ? translate(filterLabels[this.type]) : '';

    this.colorChange$
      .pipe(
        debounceTime(500),
        takeUntil(this.destroy$),
        distinctUntilChanged((prev, curr) => Utils.deepEquals(prev, curr))
      )
      .subscribe((value) => this.filtersColorChange.emit(value));
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onFiltersRemoved() {
    this.filtersRemove.emit();
  }

  get isFilterEnabled() {
    return this.isReadOnly || (this.dataSource && this.field);
  }

  get filtersLabelValue() {
    return this.filtersLabel ?? undefined;
  }

  set filtersLabelValue($event) {
    this.filtersLabelChange.emit($event);
  }

  get filtersColorValue() {
    return this.filtersColor;
  }

  get filtersValue() {
    return this.filters;
  }

  set filtersValue(filters: ComparisonFilterSet) {
    this.filtersChange.emit(filters);
  }
}
